import { useMemo } from 'react';
import { Box, Typography, Skeleton, Alert } from '@mui/material';
import DocumentsPreview from '@/components/ui/DocumentsPreview';
import DropzoneUploader from '@/components/form/DropzoneUploader';
import { useGetOpportunityMedia } from '@/lib/api/opportunities';
import {
  useGetInvestmentMedia,
  useUploadInvestmentMedia,
  useDeleteInvestmentMedia,
} from '@/lib/api/investments';

interface DocumentsTabProps {
  opportunityId?: string;
  investmentId: string;
  canEdit?: boolean;
}

export const DocumentsTab = ({
  opportunityId,
  investmentId,
  canEdit = false,
}: DocumentsTabProps) => {
  const opportunityMediaQuery = useGetOpportunityMedia(investmentId ? undefined : opportunityId);
  const investmentMediaQuery = useGetInvestmentMedia(investmentId);
  const { mutateAsync: uploadInvestmentMedia } = useUploadInvestmentMedia();
  const { mutateAsync: deleteInvestmentMedia } = useDeleteInvestmentMedia();

  const { data: media = [], isLoading } = investmentId
    ? investmentMediaQuery
    : opportunityMediaQuery;

  const documents = useMemo(
    () =>
      media
        ?.filter((m) => m.type === 'DOCUMENT')
        .map((m) => ({
          id: m.id,
          type: m.mimeType,
          url: m.signedUrl,
          name: m.fileName,
        })) ?? [],
    [media]
  );

  if (isLoading) {
    return <Skeleton variant="rectangular" height={150} />;
  }

  if (!investmentId || !canEdit) {
    return (
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Documentos
        </Typography>
        {documents.length === 0 ? (
          <Alert severity="info">No hay documentos disponibles.</Alert>
        ) : (
          <DocumentsPreview
            files={documents.map((document) => ({
              id: document.id,
              signedUrl: document.url,
              name: document.name,
              type: document.type,
            }))}
          />
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Documentos
      </Typography>
      <Box mb={2}>
        <Alert severity="info" sx={{ alignItems: 'center' }}>
          Los documentos subidos aquí son <b>privados</b> y solo visibles para ti y el PSI/inversor
          que trabaje contigo en esta operación.
        </Alert>
      </Box>
      <DropzoneUploader
        handleUploadFile={async (file) =>
          uploadInvestmentMedia({
            file,
            investmentId,
            fileType: 'DOCUMENT',
          })
        }
        onDeleteExistingFile={(id) => deleteInvestmentMedia(id)}
        existingFiles={documents}
        uploadType="documents"
      />
    </Box>
  );
};
